import * as THREE from 'three';
import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchContour,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchSlotObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
    type SketchContour,
} from '../../mesh';
import type {
    SketchArcObject,
    SketchCircleObject,
    SketchEllipseObject,
    SketchGroup,
    SketchLineObject,
    SketchSplineObject,
    SketchStyleSplineObject,
} from '../../mesh/sketch';
import { attachProfileCurveSegmentsToRegionGeometry } from './profileCurveSegments';
import { resolveProfileRegionGeometriesFromRuntime } from './resolveProfileRegionGeometriesFromRuntime';
import { resyncProfileRegionGeometryOuterFromSegments } from './offsetProfileRegionGeometry';
import { resolveSketchRegions } from '../../sketch-analysis';
import type {
    ResolvedSketchProfileFeatureBindingValue,
} from '../../feature/binding/FeatureReferenceBinding';
import type {Viewer} from '../../viewer';

/**
 * 解析对象所属草图组。
 * @param object 起始对象。
 * @returns 草图组；未命中时返回 null。
 */
function resolveSketchGroup(object: THREE.Object3D | null): SketchGroup | null {
    let current: THREE.Object3D | null = object;
    while (current) {
        if (Reflect.get(current, 'isSketchGroup') === true) {
            return current as SketchGroup;
        }
        current = current.parent;
    }
    return null;
}

/**
 * 读取草图对象的持久身份。
 *
 * 说明：跨会话引用（特征绑定）一律锚定持久 ID；派生显示对象（如轮廓）没有持久身份时返回 null。
 *
 * @param object 场景对象。
 * @returns 持久 ID；不具备持久身份时返回 null。
 */
function resolveSketchObjectPersistentId(object: THREE.Object3D | null | undefined): string | null {
    const persistentId = object ? Reflect.get(object, 'persistentId') : null;
    return typeof persistentId === 'string' && persistentId.length > 0 ? persistentId : null;
}

/**
 * 按持久 ID 在草图组内查找实体。
 *
 * 说明：解析链会被历史载荷与测试替身复用，这里对不具备持久身份索引的草图组保持安全降级。
 *
 * @param sketchGroup 草图组。
 * @param persistentId 持久 ID。
 * @returns 命中的场景对象；未命中或不支持时返回 null。
 */
function findSketchObjectByPersistentId(
    sketchGroup: SketchGroup,
    persistentId: string,
): THREE.Object3D | null {
    const lookup = Reflect.get(sketchGroup, 'getObjectByPersistentId');
    if (typeof lookup !== 'function') {
        return null;
    }
    const matched = lookup.call(sketchGroup, persistentId);
    return matched instanceof THREE.Object3D ? matched : null;
}

/**
 * 从轮廓来源对象解析当前轮廓对象。
 * @param sourceObject 轮廓来源对象。
 * @returns 当前轮廓对象；未命中时返回 null。
 */
function resolveContourFromSourceObject(sourceObject: THREE.Object3D): SketchContour | null {
    const directContour = Reflect.get(sourceObject, 'contour');
    if (directContour && isSketchContour(directContour)) {
        return directContour;
    }

    const contourGetter = Reflect.get(sourceObject, 'getContour');
    if (typeof contourGetter === 'function') {
        const contour = contourGetter.call(sourceObject);
        if (contour && isSketchContour(contour)) {
            return contour;
        }
    }

    const canCreateContour = Reflect.get(sourceObject, 'canCreateContour');
    const createContour = Reflect.get(sourceObject, 'createContour');
    if (typeof canCreateContour === 'function' && typeof createContour === 'function') {
        try {
            if (canCreateContour.call(sourceObject)) {
                const contour = createContour.call(sourceObject);
                if (contour && isSketchContour(contour)) {
                    return contour;
                }
            }
        } catch {
            return null;
        }
    }

    return null;
}

/**
 * 从轮廓对象构建区域几何。
 * @param contour 草图轮廓对象。
 * @returns 区域几何；无效时返回 null。
 */
export function buildProfileRegionGeometryFromSketchContour(contour: SketchContour): ProfileRegionGeometry | null {
    const source = contour.source;
    const referencePlane = source?.referencePlane;
    if (!referencePlane) {
        return null;
    }

    const points = contour.getContourPoints()
        .map((point) => [point.x, point.y, point.z] as [number, number, number]);
    if (points.length < 3) {
        return null;
    }

    return attachProfileCurveSegmentsToRegionGeometry({
        outer: points,
        holes: [],
        normal: [referencePlane.normal.x, referencePlane.normal.y, referencePlane.normal.z],
        xAxis: [referencePlane.xAxis.x, referencePlane.xAxis.y, referencePlane.xAxis.z],
        yAxis: [referencePlane.yAxis.x, referencePlane.yAxis.y, referencePlane.yAxis.z],
    }, contour);
}

/**
 * 解析持久化 fallback 区域几何。
 *
 * 说明：旧版本 fallback 可能只保存采样多边形，没有 `outerSegments`。只要绑定里还保存了
 * runtime 草图实体，就优先用同一套区域解析器重建精确曲线段，避免闭合样条在重建或编辑时退回折线。
 *
 * @param binding 草图轮廓绑定。
 * @returns 解析后的区域几何。
 */
function resolveFallbackRegionGeometries(
    binding: SerializedSketchProfileFeatureBinding,
): ProfileRegionGeometry[] {
    const fallbackRegionGeometries = Array.isArray(binding.fallbackRegionGeometries)
        ? binding.fallbackRegionGeometries
        : [];
    return resolveProfileRegionGeometriesFromRuntime({
        sketchId: binding.sketchId,
        fallbackRegionGeometries,
        contourKeys: Array.isArray(binding.contourKeys) ? [...binding.contourKeys] : [],
        runtimeSketchPlane: binding.runtimeSketchPlane,
        runtimeSketchEntities: binding.runtimeSketchEntities,
    });
}

/**
 * 构建草图平面运行时快照。
 * @param sketchGroup 草图组。
 * @returns 草图平面快照。
 */
export function buildRuntimeSketchPlaneFromGroup(sketchGroup: SketchGroup): RuntimeSketchPlaneInput {
    return {
        origin: sketchGroup.referencePlane.originWorld.toArray() as [number, number, number],
        normal: sketchGroup.referencePlane.normal.toArray() as [number, number, number],
        xAxis: sketchGroup.referencePlane.xAxis.toArray() as [number, number, number],
        yAxis: sketchGroup.referencePlane.yAxis.toArray() as [number, number, number],
    };
}

/**
 * 序列化草图线段运行时几何。
 * @param line 草图线段。
 * @returns Worker 可消费的线段实体；构造线返回 null。
 */
function serializeRuntimeSketchLine(line: SketchLineObject): RuntimeSketchEntityInput | null {
    if (line.isConstruction) {
        return null;
    }
    return {
        entityId: line.uuid,
        persistentId: line.persistentId,
        entityType: 'line',
        geometry: {
            start: line.startPoint.getPosition().toArray(),
            end: line.endPoint.getPosition().toArray(),
            isClosed: false,
        },
    };
}

/**
 * 序列化草图圆运行时几何。
 * @param circle 草图圆。
 * @returns Worker 可消费的圆实体；构造线返回 null。
 */
function serializeRuntimeSketchCircle(circle: SketchCircleObject): RuntimeSketchEntityInput | null {
    if (circle.isConstruction) {
        return null;
    }
    return {
        entityId: circle.uuid,
        persistentId: circle.persistentId,
        entityType: 'circle',
        geometry: {
            center: circle.getCenterWorld().toArray(),
            radius: circle.getRadius(),
            xAxis: circle.referencePlane.xAxis.toArray(),
            yAxis: circle.referencePlane.yAxis.toArray(),
            isClosed: true,
        },
    };
}

/**
 * 序列化草图圆弧运行时几何。
 * @param arc 草图圆弧。
 * @returns Worker 可消费的圆弧实体；构造线返回 null。
 */
function serializeRuntimeSketchArc(arc: SketchArcObject): RuntimeSketchEntityInput | null {
    if (arc.isConstruction) {
        return null;
    }
    const definition = arc.getArcDefinition();
    return {
        entityId: arc.uuid,
        persistentId: arc.persistentId,
        entityType: 'arc',
        geometry: {
            center: definition.centerWorld.toArray(),
            radius: definition.radius,
            startAngle: definition.startAngle,
            endAngle: definition.endAngle,
            clockwise: definition.clockwise,
            xAxis: arc.referencePlane.xAxis.toArray(),
            yAxis: arc.referencePlane.yAxis.toArray(),
            isClosed: arc.isClosedGeometry,
        },
    };
}

/**
 * 序列化草图椭圆运行时几何。
 * @param ellipse 草图椭圆。
 * @returns Worker 可消费的椭圆实体；构造线返回 null。
 */
function serializeRuntimeSketchEllipse(ellipse: SketchEllipseObject): RuntimeSketchEntityInput | null {
    if (ellipse.isConstruction) {
        return null;
    }
    const definition = ellipse.getEllipseDefinition();
    return {
        entityId: ellipse.uuid,
        persistentId: ellipse.persistentId,
        entityType: 'ellipse',
        geometry: {
            center: definition.centerWorld.toArray(),
            radiusX: definition.radiusX,
            radiusY: definition.radiusY,
            rotation: definition.rotation,
            startAngle: definition.startAngle,
            endAngle: definition.endAngle,
            clockwise: definition.clockwise,
            xAxis: ellipse.referencePlane.xAxis.toArray(),
            yAxis: ellipse.referencePlane.yAxis.toArray(),
            isClosed: ellipse.canCreateContour(),
        },
    };
}

/**
 * 序列化草图样条运行时几何。
 * @param spline 普通样条或样式样条对象。
 * @param entityType 运行时实体类型。
 * @returns Worker 可消费的样条实体；构造线返回 null。
 */
function serializeRuntimeSketchSpline(
    spline: SketchSplineObject | SketchStyleSplineObject,
    entityType: 'spline' | 'style_spline',
): RuntimeSketchEntityInput | null {
    if (spline.isConstruction) {
        return null;
    }
    const curvePoints = spline.getCurvePoints().map((point) => point.toArray());
    const controlPoints = spline.getControlPoints().map((point) => point.getPosition().toArray());
    const curveMetadata = spline.getCurveMetadata();
    return {
        entityId: spline.uuid,
        persistentId: spline.persistentId,
        entityType,
        geometry: {
            points: curvePoints,
            curvePoints,
            controlPoints,
            curveKind: curveMetadata.curveKind,
            degree: curveMetadata.degree,
            weights: [...curveMetadata.weights],
            isRational: curveMetadata.isRational,
            closureKind: curveMetadata.closureKind,
            isClosed: spline.isClosedGeometry,
        },
    };
}

/**
 * 将运行时草图实体加入去重结果。
 * @param entities 输出实体数组。
 * @param usedIds 已写入实体 ID 集合。
 * @param entity 待写入实体。
 */
function pushRuntimeSketchEntity(
    entities: RuntimeSketchEntityInput[],
    usedIds: Set<string>,
    entity: RuntimeSketchEntityInput | null,
): void {
    if (!entity || usedIds.has(entity.entityId)) {
        return;
    }
    usedIds.add(entity.entityId);
    entities.push(entity);
}

/**
 * 构建草图实体运行时快照。
 * @param sketchGroup 草图组。
 * @returns 草图实体快照列表。
 */
export function buildRuntimeSketchEntitiesFromGroup(sketchGroup: SketchGroup): RuntimeSketchEntityInput[] {
    const entities: RuntimeSketchEntityInput[] = [];
    const usedIds = new Set<string>();

    sketchGroup.getSketchObjects().forEach((object) => {
        if (isSketchLineObject(object)) {
            pushRuntimeSketchEntity(entities, usedIds, serializeRuntimeSketchLine(object));
            return;
        }
        if (isSketchCircleObject(object)) {
            pushRuntimeSketchEntity(entities, usedIds, serializeRuntimeSketchCircle(object));
            return;
        }
        if (isSketchArcObject(object)) {
            pushRuntimeSketchEntity(entities, usedIds, serializeRuntimeSketchArc(object));
            return;
        }
        if (isSketchEllipseObject(object)) {
            pushRuntimeSketchEntity(entities, usedIds, serializeRuntimeSketchEllipse(object));
            return;
        }
        if (isSketchSplineObject(object)) {
            pushRuntimeSketchEntity(entities, usedIds, serializeRuntimeSketchSpline(object, 'spline'));
            return;
        }
        if (isSketchStyleSplineObject(object)) {
            pushRuntimeSketchEntity(entities, usedIds, serializeRuntimeSketchSpline(object, 'style_spline'));
            return;
        }
        if (isSketchSlotObject(object)) {
            object.getEdges().forEach((edge) => {
                if (isSketchLineObject(edge)) {
                    pushRuntimeSketchEntity(entities, usedIds, serializeRuntimeSketchLine(edge));
                    return;
                }
                if (isSketchArcObject(edge)) {
                    pushRuntimeSketchEntity(entities, usedIds, serializeRuntimeSketchArc(edge));
                }
            });
            return;
        }

        const geometryData = Reflect.get(object, 'geometryData') as Record<string, unknown> | undefined;
        if (geometryData) {
            pushRuntimeSketchEntity(entities, usedIds, {
                entityId: object.uuid,
                persistentId: object.persistentId,
                entityType: object.type,
                geometry: structuredClone(geometryData),
            });
        }
    });
    return entities;
}

/**
 * 从绑定读取“区域选择器”序列。
 *
 * 自动识别的闭合区域没有单一场景源对象，只有 `semanticKey:locatorKey` 形式的区域键
 * 与 `regionIdentities[].semanticKey`。这里按 `contourKeys` 顺序产出选择器，缺 `contourKeys`
 * 时退回 `regionIdentities` 的语义键，保证与 `regionGeometries` 的顺序一致。
 *
 * @param binding 绑定载荷。
 * @returns 区域选择器数组；不含区域身份时为空。
 */
function readRegionSelectors(binding: SerializedSketchProfileFeatureBinding): string[] {
    const contourKeys = Array.isArray(binding.contourKeys)
        ? binding.contourKeys.filter((value): value is string => typeof value === 'string' && value.length > 0)
        : [];
    // 区域键形如 `sk_xxx:single`；对象键是场景 uuid/持久 ID，不含该前缀，需交给对象解析链。
    if (contourKeys.some((key) => key.startsWith('sk_'))) {
        return contourKeys;
    }
    return Array.isArray(binding.regionIdentities)
        ? binding.regionIdentities
            .map((identity) => identity?.semanticKey)
            .filter((value): value is string => typeof value === 'string' && value.length > 0)
        : [];
}

/**
 * 按语义身份把区域选择器匹配到实时区域并重建几何。
 *
 * 设计说明：
 * 1. 自动识别区域的身份锚点是实体成员集合的哈希（`semanticKey`），坐标编辑不改成员集合，
 *    因此编辑后 `semanticKey` 不变，可据此把冻结选择器对上更新后的实时区域；
 * 2. 优先按完整区域键 `semanticKey:locatorKey` 命中，退回裸 `semanticKey`，兼容
 *    绑定只存了 `regionIdentities` 的情形；
 * 3. 只要有任一选择器落空就整体返回 null，绝不用部分实时+部分冻结拼出不一致的轮廓。
 *
 * @param liveRegions 实时草图求出的区域集合。
 * @param selectors 区域选择器（`semanticKey:locatorKey` 或裸 `semanticKey`）。
 * @returns 命中的区域几何数组与稳定区域键；任一未命中返回 null。
 */
export function matchLiveRegionsBySelectors(
    liveRegions: readonly SketchRegionPayload[],
    selectors: readonly string[],
): { regionGeometries: ProfileRegionGeometry[]; contourKeys: string[] } | null {
    if (selectors.length <= 0 || liveRegions.length <= 0) {
        return null;
    }
    const regionGeometries: ProfileRegionGeometry[] = [];
    const contourKeys: string[] = [];
    for (const selector of selectors) {
        const semanticKey = selector.split(':')[0];
        /**
         * 三级匹配：完整区域键 → 裸语义键 → 旧版语义键。
         * 旧版键回退服务“语义键剔除探针 membership 前保存的旧文档绑定”：
         * 旧算法把坐标敏感的 inside/outside 纳入哈希，旧文档保存的 semanticKey 与
         * 新算法必然不同，必须按 provenance 携带的 legacySemanticKey 命中一次，
         * 特征重建写回绑定后即迁移为新键。
         */
        const region = liveRegions.find((candidate) => candidate.contourKey === selector)
            ?? liveRegions.find((candidate) => candidate.identity.semanticKey === semanticKey)
            ?? liveRegions.find((candidate) => candidate.provenance?.legacySemanticKey === semanticKey);
        if (!region) {
            return null;
        }
        regionGeometries.push(resyncProfileRegionGeometryOuterFromSegments({
            outer: region.outer.map((point) => [...point] as [number, number, number]),
            holes: region.holes.map((hole) => hole.map((point) => [...point] as [number, number, number])),
            normal: [...region.normal] as [number, number, number],
            xAxis: [...region.xAxis] as [number, number, number],
            yAxis: [...region.yAxis] as [number, number, number],
            outerSegments: region.outerSegments?.map((segment) => structuredClone(segment)),
            holeSegments: region.holeSegments?.map((segments) => segments.map((segment) => structuredClone(segment))),
        }));
        contourKeys.push(region.contourKey);
    }
    return { regionGeometries, contourKeys };
}

/**
 * 在实时草图上按语义身份重解出区域几何。
 *
 * 该阶段只服务“既有对象解析链全部落空”的区域型绑定，不影响单闭合对象型绑定。
 *
 * @param sketchGroup 草图组。
 * @param selectors 区域选择器（`semanticKey:locatorKey` 或裸 `semanticKey`）。
 * @returns 命中的区域几何数组与稳定区域键；任一未命中返回 null。
 */
function resolveLiveRegionGeometriesByIdentity(
    sketchGroup: SketchGroup,
    selectors: string[],
): { regionGeometries: ProfileRegionGeometry[]; contourKeys: string[] } | null {
    if (selectors.length <= 0) {
        return null;
    }
    return matchLiveRegionsBySelectors(
        resolveSketchRegions(
            buildRuntimeSketchEntitiesFromGroup(sketchGroup),
            buildRuntimeSketchPlaneFromGroup(sketchGroup),
            sketchGroup.uuid,
        ),
        selectors,
    );
}

/**
 * 按绑定解析当前草图轮廓。
 * @param sketchGroup 草图组。
 * @param binding 绑定载荷。
 * @returns 解析结果；无法解析时返回 null。
 */
export function resolveSketchProfileBindingFromGroup(
    sketchGroup: SketchGroup,
    binding: SerializedSketchProfileFeatureBinding,
): ResolvedSketchProfileFeatureBindingValue | null {
    /*
     * 自动识别区域的身份是实体成员集合哈希（semanticKey），它才是区域型绑定的权威来源。
     * 轮廓显示对象是派生缓存，草图点拖动后不一定重建，用它解析会拿到旧轮廓——
     * 因此区域型绑定必须先按语义身份在实时草图重解，命中即返回最新几何。
     */
    const regionSelectors = readRegionSelectors(binding);
    if (regionSelectors.length > 0) {
        const liveRegions = resolveLiveRegionGeometriesByIdentity(sketchGroup, regionSelectors);
        if (liveRegions) {
            return {
                kind: 'sketch-profile',
                sketchId: binding.sketchId,
                contourSourceObjectIds: [],
                contourKeys: liveRegions.contourKeys,
                regionGeometries: liveRegions.regionGeometries,
                runtimeSketchPlane: buildRuntimeSketchPlaneFromGroup(sketchGroup),
                runtimeSketchEntities: buildRuntimeSketchEntitiesFromGroup(sketchGroup),
            };
        }
    }

    const resolvedContours: Array<{
        contour: SketchContour;
        sourceObjectId: string;
    }> = [];

    const sourceIds = Array.isArray(binding.contourSourceObjectIds)
        ? binding.contourSourceObjectIds.filter((value): value is string => typeof value === 'string' && value.length > 0)
        : [];

    if (sourceIds.length > 0) {
        sourceIds.forEach((sourceId) => {
            /**
             * 身份优先级：绑定存的是跨会话持久 ID，先按持久身份命中；
             * 仅当该 ID 不是草图实体持久 ID（历史载荷或派生显示对象）时才回退运行时 uuid 遍历。
             */
            let sourceObject: THREE.Object3D | null = findSketchObjectByPersistentId(sketchGroup, sourceId);
            if (!sourceObject) {
                sketchGroup.traverse((child) => {
                    if (child.uuid === sourceId) {
                        sourceObject = child;
                    }
                });
            }
            if (!sourceObject) {
                return;
            }
            const contour = resolveContourFromSourceObject(sourceObject);
            if (!contour) {
                return;
            }
            resolvedContours.push({
                contour,
                sourceObjectId: resolveSketchObjectPersistentId(sourceObject) ?? sourceId,
            });
        });
        if (resolvedContours.length !== sourceIds.length) {
            resolvedContours.length = 0;
        }
    }

    if (resolvedContours.length <= 0 && Array.isArray(binding.contourKeys) && binding.contourKeys.length > 0) {
        binding.contourKeys.forEach((contourKey) => {
            /**
             * 轮廓键同样以草图实体持久 ID 为准（解析结果会回写为源对象持久 ID），
             * 命中源对象后按需懒建轮廓；未命中再回退到派生轮廓对象的运行时 uuid。
             */
            const persistentSource = findSketchObjectByPersistentId(sketchGroup, contourKey);
            const contourFromPersistentSource = persistentSource
                ? resolveContourFromSourceObject(persistentSource)
                : null;
            if (contourFromPersistentSource) {
                resolvedContours.push({
                    contour: contourFromPersistentSource,
                    sourceObjectId: contourKey,
                });
                return;
            }
            sketchGroup.traverse((child) => {
                if (!isSketchContour(child) || child.uuid !== contourKey) {
                    return;
                }
                resolvedContours.push({
                    contour: child,
                    sourceObjectId: resolveSketchObjectPersistentId(child.source) ?? child.source.uuid,
                });
            });
        });
        if (resolvedContours.length !== binding.contourKeys.length) {
            resolvedContours.length = 0;
        }
    }

    if (resolvedContours.length <= 0) {
        const fallbackRegionGeometries = resolveFallbackRegionGeometries(binding);
        if (fallbackRegionGeometries.length <= 0) {
            return null;
        }
        return {
            kind: 'sketch-profile',
            sketchId: binding.sketchId,
            contourSourceObjectIds: [...sourceIds],
            contourKeys: Array.isArray(binding.contourKeys) ? [...binding.contourKeys] : [],
            regionGeometries: fallbackRegionGeometries,
            runtimeSketchPlane: binding.runtimeSketchPlane ? structuredClone(binding.runtimeSketchPlane) : undefined,
            runtimeSketchEntities: Array.isArray(binding.runtimeSketchEntities)
                ? binding.runtimeSketchEntities.map((entity) => structuredClone(entity))
                : undefined,
        };
    }

    const regionGeometries = resolvedContours
        .map(({contour}) => buildProfileRegionGeometryFromSketchContour(contour))
        .filter((regionGeometry): regionGeometry is ProfileRegionGeometry => regionGeometry !== null);

    if (regionGeometries.length <= 0) {
        return null;
    }

    return {
        kind: 'sketch-profile',
        sketchId: binding.sketchId,
        contourSourceObjectIds: resolvedContours.map((item) => item.sourceObjectId),
        // 轮廓显示对象可能被清理或懒创建，绑定回写必须使用源草图对象 ID 作为稳定轮廓键。
        contourKeys: resolvedContours.map((item) => item.sourceObjectId),
        regionGeometries,
        runtimeSketchPlane: buildRuntimeSketchPlaneFromGroup(sketchGroup),
        runtimeSketchEntities: buildRuntimeSketchEntitiesFromGroup(sketchGroup),
    };
}

/**
 * 在当前 Viewer 中解析草图轮廓绑定。
 * @param viewer Viewer 实例。
 * @param binding 绑定载荷。
 * @returns 解析结果；无法解析时返回 null。
 */
export function resolveSketchProfileBindingFromViewer(
    viewer: Viewer,
    binding: SerializedSketchProfileFeatureBinding,
): ResolvedSketchProfileFeatureBindingValue | null {
    const sketchGroup = viewer.sketchGroupManager?.getGroupById(binding.sketchId) ?? null;
    if (!sketchGroup) {
        const fallbackRegionGeometries = resolveFallbackRegionGeometries(binding);
        if (fallbackRegionGeometries.length <= 0) {
            return null;
        }
        return {
            kind: 'sketch-profile',
            sketchId: binding.sketchId,
            contourSourceObjectIds: Array.isArray(binding.contourSourceObjectIds) ? [...binding.contourSourceObjectIds] : [],
            contourKeys: Array.isArray(binding.contourKeys) ? [...binding.contourKeys] : [],
            regionGeometries: fallbackRegionGeometries,
            runtimeSketchPlane: binding.runtimeSketchPlane ? structuredClone(binding.runtimeSketchPlane) : undefined,
            runtimeSketchEntities: Array.isArray(binding.runtimeSketchEntities)
                ? binding.runtimeSketchEntities.map((entity) => structuredClone(entity))
                : undefined,
        };
    }
    return resolveSketchProfileBindingFromGroup(sketchGroup, binding);
}

/**
 * 从轮廓对象解析所属草图 ID。
 * @param contour 轮廓对象。
 * @returns 草图 ID；未命中时返回空字符串。
 */
export function resolveSketchIdFromContour(contour: SketchContour): string {
    return resolveSketchGroup(contour)?.uuid ?? '';
}
